import { reactive, readonly } from "vue";
import { ASSISTENT_API_HEADER_KEY, ASSISTENT_API_URL } from "@/config/backend"
import axios from "axios";
import router from "@/router";
import { jwtDecode } from "jwt-decode";

import configurationModule from "../configuration";
import devicesModule from "../devices";
import reportingModule from "../reporting";

let timer;

const state = reactive({
    token: null,
    tokenExpires: null,
    refreshToken: null,
    refreshTokenExpires: null,
    decodedToken: null,
});

const mutations = {
    setToken: (token, tokenExpires, refreshToken, refreshTokenExpires) => {
        state.token = token;
        state.tokenExpires = tokenExpires;
        state.refreshToken = refreshToken;
        state.refreshTokenExpires = refreshTokenExpires;
        state.decodedToken = jwtDecode(state.token);

        localStorage.setItem("token", state.token);
        localStorage.setItem("tokenExpires", state.tokenExpires);
        localStorage.setItem("refreshToken", state.refreshToken);
        localStorage.setItem("refreshTokenExpires", state.refreshTokenExpires);

        const timeLeft = Number(state.tokenExpires) - new Date().getTime() - 30000; // 30 Sekunden vorher
        timer = setTimeout(() => {
            actions.refreshAuthToken();
        }, timeLeft);
    },
    clearToken: () => {

        state.token = null;
        state.tokenExpires = null;
        state.refreshToken = null;
        state.refreshTokenExpires = null;
        state.decodedToken = null;

        localStorage.removeItem("token");
        localStorage.removeItem("tokenExpires");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("refreshTokenExpires");

        devicesModule.actions.resetState();

        clearTimeout(timer);
    },
    setTokenToState: (response) => {
        mutations.setToken(response.data.token, response.data.tokenExpire, response.data.refreshToken, response.data.refreshTokenExpire);
    }
};

const actions = {
    userLogin: (payload) => {
        const url = ASSISTENT_API_URL + "token/authenticate";

        const userLoginDo = {
            userId: payload.email,
            password: payload.password
        };

        return axios
            .post(
                url, userLoginDo,
                {
                    headers: {
                        'X-AAI-APPLICATIONID': ASSISTENT_API_HEADER_KEY
                    }
                }
            )
            .then((response) => {
                mutations.setTokenToState(response);

                configurationModule.actions.initBaseConfiguration();
                devicesModule.actions.reset();
                reportingModule.actions.reset();
            })
            .catch(() => {
                const errorMessage = new Error('UNKNOWN_ERROR');
                throw errorMessage;
            });
    },
    userLogout: () => {
        mutations.clearToken();

        router.push({ 'name': 'login' });
    },
    userAutoLogin: () => {
        const token = localStorage.getItem("token");
        const expiresIn = localStorage.getItem("tokenExpires");
        const timeLeft = Number(expiresIn) - new Date().getTime() - 30000; // 30 Sekunden vorher

        if (timeLeft < 0) {
            // Token ungültig -> timeout
            return;
        }

        timer = setTimeout(() => {
            actions.refreshAuthToken();
        }, timeLeft);


        if (token && timeLeft) {
            mutations.setToken(token, expiresIn, localStorage.getItem("refreshToken"), localStorage.getItem("refreshTokenExpires"));
            configurationModule.actions.initBaseConfiguration();
        }
    },
    refreshAuthToken: () => {
        const timeLeft = Number(state.tokenExpires) - new Date().getTime() - 5; // 5 Sekunden vorher

        if (timeLeft < 0) {
            actions.userLogout();
            return;
        }

        const refreshUrl = ASSISTENT_API_URL + "token/refresh";

        const tokenRefreshDo = {
            refreshToken: state.refreshToken
        };

        return axios
            .post(
                refreshUrl, tokenRefreshDo,
                {
                    headers: {
                        'X-AAI-APPLICATIONID': ASSISTENT_API_HEADER_KEY
                    }
                }
            )
            .then((response) => {
                mutations.setTokenToState(response);
            })
            .catch(() => {
                actions.userLogout()
            });
    }
}

const getters = {
    isAuthenticated: () => !!state.token,
    hasRole: (role) => {
        if (state.decodedToken && state.decodedToken.roles) {
            return state.decodedToken.roles.includes(role);
        }
        return false;
    },
    getApiHeaders: () => {
        return {
            'X-AAI-APPLICATIONID': ASSISTENT_API_HEADER_KEY,
            'Authorization': state.token
        };
    },

    canEdit: () => {
        return getters.isAuthenticated() && !getters.hasRole('ROLE_CFG_VIEW_ONLY');
    },
    isCustomer: () => {
        return getters.isAuthenticated() && (getters.hasRole('ROLE_ACCESS_CUSTOMERFRONTEND') || getters.hasRole('ROLE_ACCESS_ADMINFRONTEND'));
    },
    canSeeDeviceId: () => {
        return getters.isAuthenticated() && getters.hasRole('ROLE_SEE_DEVICEID');
    },
    getUserId: () => {
        return state.decodedToken.id;
    },
    getFirstName: () => {
        return state.decodedToken.firstname;
    },
    getLastName: () => {
        return state.decodedToken.lastname;
    },
    getEmail: () => {
        return state.decodedToken.sub;
    }
}

const authModule = {
    state: readonly(state),
    actions,
    getters
}

export default authModule;